import {defaultBoardLabels} from '@github-ui/board'
import type {BoardColumn, BoardRow} from '@github-ui/board-core'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {hashKey, useSuspenseQuery} from '@github-ui/react-query'
import {Banner} from '@primer/react'
import {type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {boardDocumentAdapter} from './board-document-adapter'
import {BoardMoveRefusal, BoardMoveRefusalContext} from './BoardMoveRefusal'
import {BoardSurface} from './generic/BoardSurface'
import type {SavedViewsBoardAdapter, SavedViewsBoardItem, SavedViewsBoardOptions} from './types'
import {useBoardAxisPaging, withLoadingAxes} from './use-board-axis-paging'
import {useBoardCellPaging, withLoadingCells} from './use-board-cell-paging'
import {useBoardItemMove} from './use-board-item-move'

interface SavedViewBoardProps<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata> {
  adapter: SavedViewsBoardAdapter<TItem, TMetadata, TPageMetadata>
  options: SavedViewsBoardOptions
  renderCard: (item: TItem) => ReactNode
  /** All items accepted into the board, including offscreen cards. Does not include optimistic moves. */
  onLoadedItemsChange?: (items: readonly TItem[]) => void
  renderColumnHeader?: (column: BoardColumn, metadata: TMetadata) => ReactNode
  renderRowHeader?: (row: BoardRow, metadata: TMetadata) => ReactNode
  /**
   * What the host says when the response filled no column. Optional, because only the host
   * knows whether that is a rejected query, a page past the end, a search that matched
   * nothing or no search at all, and its metadata is what carries the difference.
   */
  renderEmptyState?: (metadata: TMetadata) => ReactNode
  /**
   * Whether the host has a request out that replaces this board. The board on screen stays
   * drawn, busy and unusable, until it lands.
   *
   * Stated rather than read off the query, because the request is often not this one: a
   * host whose adapter answers from data the page already holds changes the board by
   * fetching the page, and nothing about that reaches here.
   */
  isLoading?: boolean
}

export function SavedViewBoard<TItem extends SavedViewsBoardItem, TMetadata = undefined, TPageMetadata = undefined>(
  props: SavedViewBoardProps<TItem, TMetadata, TPageMetadata>,
) {
  return (
    <Suspense fallback={null}>
      <SavedViewBoardContent {...props} />
    </Suspense>
  )
}

/**
 * Counts the boards that arrived while the host had a request out to replace the one on screen.
 * A board that changes with no request out, such as a refresh, is the same grid and keeps the count.
 */
function useReplacementCount(boardIdentity: string, isLoading: boolean): number {
  const [state, setState] = useState<{count: number; replacing: string | null}>({count: 0, replacing: null})
  let next = state
  if (state.replacing !== null && state.replacing !== boardIdentity) {
    next = {count: state.count + 1, replacing: isLoading ? boardIdentity : null}
  } else if (isLoading !== (state.replacing !== null)) {
    next = {count: state.count, replacing: isLoading ? boardIdentity : null}
  }
  if (next !== state) setState(next)
  return next.count
}

function SavedViewBoardContent<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata>({
  adapter,
  options,
  renderCard,
  onLoadedItemsChange,
  renderColumnHeader,
  renderRowHeader,
  renderEmptyState,
  isLoading = false,
}: SavedViewBoardProps<TItem, TMetadata, TPageMetadata>) {
  const queryKey = useMemo(() => ['saved-views-board', adapter.key, options], [adapter.key, options])
  const boardIdentity = useMemo(() => hashKey(queryKey), [queryKey])
  const replacements = useReplacementCount(boardIdentity, isLoading)
  // Read once, because it also decides the staleness below and the two have to agree about
  // whether this board came with the page.
  const initialPayload = useMemo(() => adapter.readInitialBoardPayload?.(options), [adapter, options])
  const {data: base} = useSuspenseQuery({
    queryKey,
    queryFn: ({signal}) => adapter.fetchInitialBoardPayload({...options, signal}),
    // Seeds the query only while it holds nothing, so an adapter that answers keeps the board on
    // the server and through hydration, and every read after that is the fetched payload.
    initialData: initialPayload,
    // A board that arrived with the page is replayed verbatim by its adapter, so refetching it
    // can only discard the pages its cells have been given since.
    //
    // Spread rather than stated as undefined, which would replace the query client's configured
    // default rather than defer to it. Keyed on the payload, not the method, since the contract
    // lets an adapter answer with nothing and then fetch like any other.
    ...(initialPayload ? {staleTime: Infinity} : {}),
  })

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- Schedules external prefetches from accepted query data, not mirrored parent state.
    onLoadedItemsChange?.(base.boardData.data.cells.flatMap(cell => cell.items))
  }, [base.boardData.data.cells, onLoadedItemsChange])

  // The overlay is computed here and never written to the cache, so dropping a pending move
  // is the whole of the rollback. The same adapter is named on the cache target, because a
  // target never inherits the view's: a confirmed placement has to reach `base` before the
  // overlay drops, or a successful move flickers back through the cell it came from.
  //
  // No `revalidate`. The board is read from an index that has not yet seen the write, so
  // refetching here is a successful move visibly snapping back. Reconciling that until the
  // index catches up is the ledger, github/views#105.
  const targets = useMemo(() => [{queryKey, documentAdapter: boardDocumentAdapter}], [queryKey])
  const data = useOptimisticData(base, {documentAdapter: boardDocumentAdapter, targets})

  const {onLoadMoreItems, loadingCells} = useBoardCellPaging({adapter, options, queryKey})
  const {onLoadMoreColumns, onLoadMoreRows, onVisibleKeysChange, loadingAxes, invalidatePrefetchedPages} =
    useBoardAxisPaging({
      adapter,
      options,
      queryKey,
    })

  // Declared after the paging it invalidates: a page read before a move describes an index
  // without it.
  const {onItemsMove, getItemDragState, getMoveRefusal, canDropItem, moveError, clearMoveError} = useBoardItemMove({
    adapter,
    options,
    boardData: data.boardData,
    onBoardWrite: invalidatePrefetchedPages,
  })
  const boardData = useMemo(
    () => withLoadingAxes(withLoadingCells(data.boardData, loadingCells), loadingAxes),
    [data.boardData, loadingAxes, loadingCells],
  )

  // Keyed on the metadata rather than on the whole payload: a pending move gives the payload a
  // new identity, and rebuilding these would hand the board new render callbacks and redraw
  // every visible cell and card for a change to one of them.
  const {metadata} = data
  const renderHeader = useMemo(
    () => (renderColumnHeader ? (column: BoardColumn) => renderColumnHeader(column, metadata) : undefined),
    [renderColumnHeader, metadata],
  )
  const renderSwimlaneHeader = useMemo(
    () => (renderRowHeader ? (row: BoardRow) => renderRowHeader(row, metadata) : undefined),
    [renderRowHeader, metadata],
  )
  const renderEmpty = useMemo(
    () => (renderEmptyState ? () => renderEmptyState(metadata) : undefined),
    [renderEmptyState, metadata],
  )
  const renderCardWithRefusal = useCallback(
    (item: TItem) => (
      <>
        {renderCard(item)}
        <BoardMoveRefusal item={item} />
      </>
    ),
    [renderCard],
  )

  const surfaceRef = useRef<HTMLDivElement>(null)
  // The dismiss button unmounts with the banner, so focus goes back to the board. From its
  // `<section>`, the next Tab reaches the card the reader left off at.
  const dismissMoveError = useCallback(() => {
    surfaceRef.current?.querySelector<HTMLElement>('section')?.focus()
    clearMoveError()
  }, [clearMoveError])

  return (
    <>
      {moveError ? <Banner variant="critical" title={moveError} onDismiss={dismissMoveError} /> : null}
      <BoardMoveRefusalContext value={onItemsMove ? getMoveRefusal : undefined}>
        <BoardSurface
          // board-core's sentinels hold "already asked" in refs that survive new data on purpose, so
          // a board replacing one cell for cell would inherit it and never ask for its own first
          // page. Remounting is what states this is a different grid.
          key={replacements}
          ref={surfaceRef}
          boardData={boardData}
          renderCard={renderCardWithRefusal}
          renderColumnHeader={renderHeader}
          renderRowHeader={renderSwimlaneHeader}
          renderEmptyState={renderEmpty}
          onLoadMoreItems={onLoadMoreItems}
          onLoadMoreColumns={onLoadMoreColumns}
          onLoadMoreRows={onLoadMoreRows}
          onVisibleKeysChange={onVisibleKeysChange}
          isLoading={isLoading}
          labels={defaultBoardLabels}
          onItemsMove={onItemsMove}
          getItemDragState={getItemDragState}
          canDropItem={canDropItem}
        />
      </BoardMoveRefusalContext>
    </>
  )
}
